import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './BridgeClubPages.css';

interface MemberCloseToReward {
  id: number;
  name: string;
  email: string;
  store_name: string;
  bags_purchased: number;
  loyalty_progress: number;
}

interface AvailableReward {
  id: string;
  member_name: string;
  member_email: string;
  store_name: string;
  redemption_code: string;
  reward_type: string;
  reward_value: number;
  reward_description: string;
  discount_type: string;  // Legacy
  discount_value: number;  // Legacy
  created_at: string;
  expires_at: string;
  status: string;
}

interface RedeemedReward {
  id: string;
  member_name: string;
  store_name: string;
  redeemed_at_store_name: string;
  redemption_code: string;
  reward_type: string;
  reward_value: number;
  reward_description: string;
  used_at: string;
  redeemed_at: string;
  verified_by: string;
  redeemed_by: string;
}

interface RewardsStats {
  total_bags_purchased: number;
  rewards_earned: number;
  rewards_redeemed: number;
  pending_rewards: number;
}

interface RewardsData {
  close_to_reward: MemberCloseToReward[];
  available_rewards: AvailableReward[];
  redeemed_rewards: RedeemedReward[];
  stats: RewardsStats;
}

const BridgeClubRewards: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<RewardsData>({
    close_to_reward: [],
    available_rewards: [],
    redeemed_rewards: [],
    stats: {
      total_bags_purchased: 0,
      rewards_earned: 0,
      rewards_redeemed: 0,
      pending_rewards: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'close' | 'available' | 'redeemed'>('close');

  const fetchRewards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<RewardsData>('/admin/bridge-club/rewards/');
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load rewards:', err);
      setError(err.response?.data?.detail || 'Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Expired';
    if (diff === 0) return 'Today';
    if (diff === 1) return '1 day';
    return `${diff} days`;
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bridge-club-page">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club')}>
            &larr; Back to Bridge Club
          </button>
          <h1>Bridge Club Rewards</h1>
          <p>Loyalty program management</p>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchRewards}>Retry</button>
        </div>
      )}

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{data.stats.total_bags_purchased.toLocaleString()}</div>
          <div className="stat-label">Total Bags Purchased</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.stats.rewards_earned.toLocaleString()}</div>
          <div className="stat-label">Rewards Earned</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.stats.rewards_redeemed.toLocaleString()}</div>
          <div className="stat-label">Rewards Redeemed</div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-value">{data.stats.pending_rewards.toLocaleString()}</div>
          <div className="stat-label">Pending Rewards</div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="section-tabs">
        <button
          className={`tab-button ${activeSection === 'close' ? 'active' : ''}`}
          onClick={() => setActiveSection('close')}
        >
          Close to Earning ({data.close_to_reward.length})
        </button>
        <button
          className={`tab-button ${activeSection === 'available' ? 'active' : ''}`}
          onClick={() => setActiveSection('available')}
        >
          Available Rewards ({data.available_rewards.length})
        </button>
        <button
          className={`tab-button ${activeSection === 'redeemed' ? 'active' : ''}`}
          onClick={() => setActiveSection('redeemed')}
        >
          Redeemed ({data.redeemed_rewards.length})
        </button>
      </div>

      {/* Close to Earning */}
      {activeSection === 'close' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Close to Earning (7-9 bags)</h2>
            <p>These members are almost at their 10-bag reward!</p>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Store</th>
                  <th>Bags Purchased</th>
                  <th>Progress</th>
                  <th>Bags Needed</th>
                </tr>
              </thead>
              <tbody>
                {data.close_to_reward.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      No members close to earning a reward
                    </td>
                  </tr>
                ) : (
                  data.close_to_reward.map(member => (
                    <tr key={member.id}>
                      <td>
                        <div className="member-info">
                          <span className="member-name">{member.name}</span>
                          <span className="member-email">{member.email}</span>
                        </div>
                      </td>
                      <td>{member.store_name}</td>
                      <td className="center">{member.bags_purchased}</td>
                      <td>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar"
                            style={{ width: `${member.loyalty_progress * 10}%` }}
                          ></div>
                          <span className="progress-text">{member.loyalty_progress}/10</span>
                        </div>
                      </td>
                      <td className="center highlight-text">
                        {10 - member.loyalty_progress} more
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Available Rewards */}
      {activeSection === 'available' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Available Rewards</h2>
            <p>Members with earned rewards ready to redeem</p>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Store</th>
                  <th>Reward Code</th>
                  <th>Discount</th>
                  <th>Earned</th>
                  <th>Expires In</th>
                </tr>
              </thead>
              <tbody>
                {data.available_rewards.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-row">
                      No available rewards
                    </td>
                  </tr>
                ) : (
                  data.available_rewards.map(reward => (
                    <tr key={reward.id}>
                      <td>
                        <div className="member-info">
                          <span className="member-name">{reward.member_name}</span>
                          <span className="member-email">{reward.member_email}</span>
                        </div>
                      </td>
                      <td>{reward.store_name}</td>
                      <td>
                        <code className="reward-code">{reward.redemption_code}</code>
                      </td>
                      <td>
                        {reward.reward_type === 'free_bag'
                          ? `${reward.reward_value} FREE bag`
                          : reward.discount_type === 'percent'
                          ? `${reward.discount_value}% off`
                          : `$${reward.discount_value} off`}
                      </td>
                      <td>{formatDate(reward.created_at)}</td>
                      <td className={getDaysUntilExpiry(reward.expires_at) === 'Expired' ? 'text-danger' : ''}>
                        {getDaysUntilExpiry(reward.expires_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Redeemed Rewards */}
      {activeSection === 'redeemed' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Redeemed Rewards</h2>
            <p>History of all redeemed loyalty rewards</p>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Home Store</th>
                  <th>Redeemed At</th>
                  <th>Code</th>
                  <th>Reward</th>
                  <th>Date</th>
                  <th>Processed By</th>
                </tr>
              </thead>
              <tbody>
                {data.redeemed_rewards.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-row">
                      No redeemed rewards yet
                    </td>
                  </tr>
                ) : (
                  data.redeemed_rewards.map(reward => (
                    <tr key={reward.id}>
                      <td>{reward.member_name}</td>
                      <td>{reward.store_name}</td>
                      <td>{reward.redeemed_at_store_name || reward.store_name}</td>
                      <td>
                        <code className="reward-code">{reward.redemption_code}</code>
                      </td>
                      <td>
                        {reward.reward_type === 'free_bag'
                          ? `${reward.reward_value} FREE bag`
                          : reward.reward_description || 'FREE bag'}
                      </td>
                      <td>{formatDate(reward.redeemed_at || reward.used_at)}</td>
                      <td>{reward.redeemed_by || reward.verified_by || 'System'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeClubRewards;
